import { PositionSelectType } from '@/utils/constant';
import { HttpStatusCode } from '@/fetch/core/constant';
import { mapEditorFetch } from '../utils/fetch';
import { useSearchStore } from '../store/searchStore';
import type { SearchFormType } from '../utils/config';
import type { IControllerBridge } from './strategies/types';

/**
 * 搜索管理器
 *
 * 层级：controller（"业务怎么做"）
 *
 * 职责：
 * 1. 搜索选项懒加载 — 站点/停靠点/车辆列表，按需请求，loaded 标记去重
 * 2. 搜索回调 — onSearch 处理 5 种搜索类型切换，onConfirm 处理地理位置确认搜索
 * 3. UI 状态桥接 — locationOptionList / isLocationDropdownOpen 通过 setUICallbacks 注入，
 *    由 useMapBootstrap 管理 React 侧的 useState
 */
export class SearchManager {
  private loaded = { station: false, stop: false, vehicle: false };

  // UI 回调（由 useMapBootstrap 注入）
  private updateLocationOptions: (list: any[]) => void = () => {};
  private updateDropdownOpen: (open: boolean) => void = () => {};
  private updateDynamicMap:
    | ((pos: { lon: number; lat: number }, extra: any) => void)
    | null = null;

  /** 注入 UI 回调 */
  setUICallbacks(opts: {
    updateLocationOptions: (list: any[]) => void;
    updateDropdownOpen: (open: boolean) => void;
    updateDynamicMap: (pos: { lon: number; lat: number }, extra: any) => void;
  }) {
    this.updateLocationOptions = opts.updateLocationOptions;
    this.updateDropdownOpen = opts.updateDropdownOpen;
    this.updateDynamicMap = opts.updateDynamicMap;
  }

  /** 选择框类型切换回调 */
  async onSearch(params: Partial<SearchFormType>) {
    const store = useSearchStore.getState();
    const searchType =
      params.searchType ?? store.positionSearchVal.searchType;

    if (searchType === PositionSelectType.position) {
      store.setPositionSearchVal((prev) => ({
        ...prev,
        searchType,
        value: params.value ?? null,
        latlngForm: null,
      }));
      if (!params.value) {
        this.updateLocationOptions([]);
        this.updateDropdownOpen(false);
      }
      return;
    }

    if (searchType === PositionSelectType.station) {
      this.updateLocationOptions([]);
      this.updateDropdownOpen(false);
      store.setPositionSearchVal((prev) => ({
        ...prev,
        value: null,
        searchType,
        latlngForm: null,
      }));
      await this.ensureOptions(searchType);
      return;
    }

    if (searchType === PositionSelectType.stop) {
      this.updateLocationOptions([]);
      this.updateDropdownOpen(false);
      const nextState = {
        ...store.positionSearchVal,
        ...params,
        searchType,
        stopValue: params.stopValue ?? null,
        value: null,
        latlngForm: null,
      };
      store.setPositionSearchVal((prev) => ({
        ...prev,
        ...params,
        searchType,
        stopValue: params.stopValue ?? null,
        value: null,
        latlngForm: null,
      }));
      await this.ensureOptions(searchType, nextState.stopValue);
      return;
    }

    if (searchType === PositionSelectType.vehicle) {
      this.updateLocationOptions([]);
      this.updateDropdownOpen(false);
      store.setPositionSearchVal((prev) => ({
        ...prev,
        ...params,
        searchType,
        vehicleValue: params.vehicleValue ?? null,
        value: null,
        latlngForm: null,
      }));
      await this.ensureOptions(searchType);
      return;
    }

    if (searchType === PositionSelectType.latLag) {
      this.updateLocationOptions([]);
      this.updateDropdownOpen(false);
      store.setPositionSearchVal((prev) => ({
        ...prev,
        ...params,
        searchType,
        value: null,
      }));
    }
  }

  /** 确认搜索 */
  onConfirm(params: Partial<SearchFormType>, _ctrl: IControllerBridge) {
    const position = params.value?.split(',');
    if (position && this.updateDynamicMap) {
      this.updateDynamicMap(
        {
          lon: Number(position[0]),
          lat: Number(position[1]),
        },
        null
      );
      const store = useSearchStore.getState();
      store.setPositionSearchVal((prev) => ({
        ...prev,
        searchType: PositionSelectType.position,
        value: (params as any).locationKeyword ?? prev.value,
      }));
      this.updateDropdownOpen(false);
    }
  }

  /** 确保搜索类型选项已加载 */
  async ensureOptions(type: string, keyword?: string | null) {
    if (type === PositionSelectType.station) {
      if (!this.loaded.station) {
        await this.fetchCityAndStationList();
      }
      return;
    }

    if (type === PositionSelectType.stop) {
      const stopKeyword = keyword?.trim();
      if (stopKeyword) {
        await this.handleStopListInfo(stopKeyword);
        // 关键字搜索会覆盖 store 中的完整列表，必须重置标记，
        // 否则清空输入后 ensureOptions 认为已加载过而跳过请求。
        this.loaded.stop = false;
      } else if (!this.loaded.stop) {
        await this.handleStopListInfo(null);
        this.loaded.stop = true;
      }
      return;
    }

    if (type === PositionSelectType.vehicle && !this.loaded.vehicle) {
      await this.getVehicleList();
    }
  }

  /** 初始加载（组件挂载时调用一次） */
  async init() {
    await this.ensureOptions(
      useSearchStore.getState().positionSearchVal.searchType
    );
  }

  // ---- 内部方法 ----

  /** 获取站点级联下拉框 */
  private async fetchCityAndStationList() {
    const res: any = await mapEditorFetch.fetchCityAndStationList();
    const temp: any = res.data?.map((item: any) => {
      const tempArr: any[] = [];
      item?.child &&
        item?.child.forEach((ele: any) => {
          const { id, name } = ele;
          tempArr.push({ value: id, label: name });
        });
      return {
        value: item.id,
        label: item.name,
        children: tempArr,
      };
    });
    useSearchStore.getState().setCityList(temp);
    this.loaded.station = true;
  }

  /** 模糊搜索停靠点列表 */
  private async handleStopListInfo(name: any) {
    const res = await mapEditorFetch.fetchStopList(name);
    if (res.code === HttpStatusCode.Success) {
      const temp = Array.isArray(res.data)
        ? res.data?.map((item: any) => ({
          value: item.id,
          label: item.name,
        }))
        : [];
      useSearchStore.getState().setStopOptionList(temp);
    }
  }

  /** 获取车辆列表 */
  private async getVehicleList() {
    const res = await mapEditorFetch.fetchVehicleList();
    if (res.code === HttpStatusCode.Success) {
      const temp = res.data?.map((item: any) => ({
        value: item.name,
        label: item.name,
      }));
      useSearchStore.getState().setVehicleOptionList(temp);
      this.loaded.vehicle = true;
    }
  }
}
